import React from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// 10 data columns total. Widths below sum to a fixed table width so every
// owner's table renders identically, and each header colSpan={5} block
// covers exactly half the columns (5 + 5 = 10) with no leftover cell.
const COLUMNS = [
  { label: "Route Date", width: 120 },
  { label: "Visit Purpose", width: 130 },
  { label: "Session Visit", width: 126 },
  { label: "Lifetime Visits", width: 136 },
  { label: "Date", width: 122 },
  { label: "Follow up Date", width: 125 },
  { label: "Visit Outcome", width: 150 },
  { label: "Meeting Time", width: 140 },
  { label: "Visit Time", width: 150 },
  { label: "End Visit Remark", width: 260 },
];
const TABLE_WIDTH = COLUMNS.reduce((sum, c) => sum + c.width, 0);
const BORDER = "1px solid #000";

const ViewSchoolVisitHistoryModal = ({ closePopup, modalData }) => {
  const {
    partyId,
    schoolName,
    visitCountByOwner = [],
    visitHistory = [],
  } = modalData || {};

  const textPrimary = "#000";
  const textSecondary = "#4b5563";
  const headerBg = "#1976d2";

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
     const formatDate2 = (dateStr) => {
        if (!dateStr) return "N/A";

        const date = new Date(dateStr);

        if (isNaN(date)) return "N/A";

        return date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            // hour: "2-digit",
            // minute: "2-digit",
            // second: "2-digit",
            // hour12: true
        });
    };


  const formatTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const schoolLabel = schoolName ? `${schoolName} (${partyId ?? "-"})` : "-";

  // --- Group visitHistory rows by ownerId, and pair each group with the
  // matching entry from visitCountByOwner so we can label the table with
  // the correct RM name/empId per owner. ---
  const groupedByOwner = visitHistory.reduce((acc, visit) => {
    const key = visit.ownerId || "unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(visit);
    return acc;
  }, {});

  const orderedOwnerIds = [
    ...visitCountByOwner.map((o) => o.ownerId),
    ...Object.keys(groupedByOwner).filter(
      (id) => !visitCountByOwner.some((o) => o.ownerId === id)
    ),
  ];

  const ownerGroups = orderedOwnerIds
    .filter((ownerId) => groupedByOwner[ownerId]?.length)
    .map((ownerId) => {
      const ownerInfo = visitCountByOwner.find((o) => o.ownerId === ownerId);
      const rmLabel = ownerInfo
        ? `${ownerInfo.ownerName ?? "-"} (${ownerInfo.empId ?? "-"})`
        : ownerId;
      return {
        ownerId,
        rmLabel,
        visitCount: ownerInfo?.visitCount ?? groupedByOwner[ownerId].length,
        visits: groupedByOwner[ownerId],
      };
    });

  // ---- shared cell styles: border on every side, every cell ----
  const groupHeaderCellSx = {
    color: "#000",
    backgroundColor: "#ceedfd",
    fontWeight: 700,
    fontSize: 15,
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    border: BORDER,
    py: 1,
  };

  const columnHeaderCellSx = (width) => ({
    color: "#000",
    backgroundColor: "#e2f5ff",
    fontWeight: 700,
    fontSize: 14.5,
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    boxSizing: "border-box",
    border: BORDER,
    width,
    maxWidth: width,
  });

  const bodyCellSx = (extra = {}) => ({
    color: textPrimary,
    textAlign: "center",
    fontSize: 15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    boxSizing: "border-box",
    border: BORDER,
    ...extra,
  });

  return (
    <Box
      sx={{
        backgroundColor: "#f8fafc",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          // position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e5e7eb",
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700 }}>
          Visit History ({schoolLabel})
        </Typography>

        <IconButton onClick={closePopup}>
          <CloseIcon />
        </IconButton>
      </Box>

          {/* Address comparison table: Party Address (CRM) vs Party Address (Tagged) */}
      <Box
        sx={{
          px: 3,
          pt: 2,
        }}
      >
        <Box
          sx={{
            overflowX: "auto",
            border: BORDER,
            borderRadius: 1,
          }}
        >
          <Table
            size="small"
            sx={{
              backgroundColor: "#fff",
              borderCollapse: "collapse",
              tableLayout: "fixed",
              width: "100%",
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: headerBg }}>
                <TableCell sx={columnHeaderCellSx("50%")}>
                  Party Address (CRM)
                </TableCell>
                <TableCell sx={columnHeaderCellSx("50%")}>
                  Party Address (Tagged)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  sx={bodyCellSx({
                    textAlign: "left",
                    whiteSpace: "normal",
                    fontSize: 14.5,
                  })}
                >
                  {modalData?.address || "NA"}
                </TableCell>
                <TableCell
                  sx={bodyCellSx({
                    textAlign: "left",
                    whiteSpace: "normal",
                    fontSize: 14.5,
                  })}
                >
                  {modalData?.taggedAddress || "NA"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>


      <Box
        sx={{
          top: 0,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e5e7eb",
          px: 3,
          py: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700 }}>
          Life Time Visit Count: <span className="ml-2">{modalData?.totalVisitCount || 0}</span>
        </Typography>
        <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700 }}>
          Session Wise Visit Count: <span className="ml-2">{modalData?.totalSessionWiseCount || 0}</span>
        </Typography>
      </Box>

      <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
        {ownerGroups.length > 0 ? (
          ownerGroups.map((group) => (
            <Box key={group.ownerId} sx={{ mb: 4 }}>
              <Typography
                variant="subtitle2"
                sx={{ color: textPrimary, fontWeight: 700, mb: 1 }}
              >
                {group.rmLabel} — {group.visitCount} visit
                {group.visitCount === 1 ? "" : "s"}
              </Typography>

              <Box
                sx={{
                  overflowX: "auto",
                  border: BORDER,
                  borderRadius: 1,
                }}
              >
                <Table
                  size="small"
                  sx={{
                    backgroundColor: "#fff",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                    width: "100%",
                    minWidth: TABLE_WIDTH,
                  }}
                >
                  <colgroup>
                    {COLUMNS.map((c, i) => (
                      <col key={i} style={{ width: c.width }} />
                    ))}
                  </colgroup>

                  <TableHead>
                    {/* Group header row: school name | RM label, each spanning 5 columns */}
                    <TableRow>
                      <TableCell colSpan={5} sx={groupHeaderCellSx}>
                        <Tooltip title={schoolLabel}>
                          <span>{schoolLabel}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell colSpan={5} sx={groupHeaderCellSx}>
                        <Tooltip title={group.rmLabel}>
                          <span>{group.rmLabel}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Column header row */}
                    <TableRow sx={{ backgroundColor: headerBg }}>
                      {COLUMNS.map((c) => (
                        <TableCell key={c.label} sx={columnHeaderCellSx(c.width)}>
                          {c.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {group.visits.map((visit, index) => (
                      <TableRow
                        key={visit.id ?? index}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#fafafa" : "#ffffff",
                          "&:hover": { backgroundColor: "#f1f5f9" },
                        }}
                      >
                        <TableCell sx={bodyCellSx({ maxWidth: COLUMNS[0].width })}>
                          {/* <Tooltip title={formatDate(visit.routeDate)}> */}
                           <Tooltip
                            title={formatDate2(visit.routeDate)}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                },
                              },
                            }}
                          >
                            <span>{formatDate2(visit.routeDate)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={bodyCellSx({ maxWidth: COLUMNS[1].width })}>
                          {/* <Tooltip title={visit.visitPurpose || "-"}> */}
                           <Tooltip
                            title={visit.visitPurpose || "-"}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                },
                              },
                            }}
                          >
                            <span>{visit.visitPurpose || "-"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={bodyCellSx({ maxWidth: COLUMNS[2].width })}>
                          <Tooltip
                            title={visit.sessionVisit}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                },
                              },
                            }}
                          >
                          {visit.sessionVisit ?? "-"}
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={bodyCellSx({ maxWidth: COLUMNS[3].width })}>
                          <Tooltip
                            title={visit.lifetimeVisits}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                },
                              },
                            }}
                          >
                          {visit.lifetimeVisits ?? "-"}
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={bodyCellSx({ maxWidth: COLUMNS[4].width })}>
                          {/* <Tooltip title={formatDate(visit.date) || "N/A"}> */}
                           <Tooltip
                            title={formatDate2(visit.date) || "N/A"}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                },
                              },
                            }}
                          >
                            <span>{formatDate2(visit.date) || "N/A"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={bodyCellSx({ maxWidth: COLUMNS[5].width })}>
                          {/* {formatDate(visit.followUpDate)} */}
                          {/* <Tooltip title={formatDate(visit.followUpDate)}> */}
                           <Tooltip
                            title={formatDate2(visit.followUpDate)}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                },
                              },
                            }}
                          >
                            <span>{formatDate2(visit.followUpDate)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={bodyCellSx({ maxWidth: COLUMNS[6].width })}>
                          {/* {visit.visitOutcomeName || "N/A"} */}
                          {/* <Tooltip title={visit.visitOutcomeName || "N/A"}> */}
                          <Tooltip
                            title={visit.visitOutcomeName || "N/A"}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                },
                              },
                            }}
                          >
                            <span>{visit.visitOutcomeName || "N/A"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={bodyCellSx({ maxWidth: COLUMNS[7].width })}>
                          <Tooltip
                            title={formatTime(visit.meetingTime)}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                },
                              },
                            }}
                          >
                          {formatTime(visit.meetingTime)}
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={bodyCellSx({ maxWidth: COLUMNS[8].width })}>
                          <Tooltip
                            title= {visit.visitTime &&
                            !isNaN(new Date(visit.visitTime).getTime())
                            ? formatTime(visit.visitTime)
                            : visit.visitTime || "N/A"}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                },
                              },
                            }}
                          >
                          {visit.visitTime &&
                            !isNaN(new Date(visit.visitTime).getTime())
                            ? formatTime(visit.visitTime)
                            : visit.visitTime || "N/A"}
                            </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={bodyCellSx({
                            textAlign: "left",
                            fontSize: 14.5,
                            maxWidth: COLUMNS[9].width,
                          })}
                        >
                          {/* <Tooltip title={visit.endVisitRemark || "-"} sx={{ fontSize: 15 }}> */}
                          <Tooltip
                            title={visit.endVisitRemark || "-"}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "16px",
                                },
                              },
                            }}
                          >
                            <span>{visit.endVisitRemark || "-"}</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ color: textSecondary, mb: 3 }}>
            No visit history available.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ViewSchoolVisitHistoryModal;